import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit3, Trash2, Eye, EyeOff, Crown, Search, Upload, X, Save, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TrackModal = ({ track, onClose, onSave, hideFinancials }) => {
  const [form, setForm] = useState(track || {
    title: '', artist: '', genre: '', vibe: '', bpm: 120, key: 'C',
    duration: '3:00', price: 5.99, cover_image: '', audio_preview_url: '', wav_file_url: '',
    description: '', is_premium: false, is_visible: true, license_type: 'basic', license_terms: ''
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');

  const handleUpload = async (e, type, targetField) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(type + (targetField || ''));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/api/admin/upload/${type}`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      const field = targetField || (type === 'audio' ? 'audio_preview_url' : 'cover_image');
      setForm({ ...form, [field]: r.data.url });
    } catch (err) { alert('Error subiendo: ' + (err.response?.data?.detail || err.message)); }
    setUploading('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, bpm: parseInt(form.bpm), price: parseFloat(form.price) });
    } catch (err) { alert(err.response?.data?.detail || 'Error guardando track'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="glass-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="track-form-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">{track ? 'Editar Track' : 'Agregar Track'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {[{ key: 'title', label: 'Titulo', required: true }, { key: 'artist', label: 'Artista', required: true }, { key: 'genre', label: 'Genero', required: true }, { key: 'vibe', label: 'Vibe', required: true }].map(f => (
            <div key={f.key}><label className="block text-xs text-white/50 mb-1">{f.label}</label>
              <input className="w-full input-glass" value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} required={f.required} data-testid={`track-input-${f.key}`} /></div>
          ))}
          <div>
            <label className="block text-xs text-white/50 mb-1">MP3 (Preview / Entrega)</label>
            <div className="flex gap-2">
              <input className="flex-1 input-glass text-sm" value={form.audio_preview_url} onChange={e => setForm({ ...form, audio_preview_url: e.target.value })} placeholder="URL o subir MP3" data-testid="track-input-audio_preview_url" />
              <label className={`btn-secondary py-2 px-3 text-sm cursor-pointer flex items-center gap-1 ${uploading === 'audio' ? 'opacity-50' : ''}`}>
                <Upload className="w-4 h-4" />{uploading === 'audio' ? '...' : 'Subir'}
                <input type="file" accept=".mp3,audio/mpeg" className="hidden" onChange={e => handleUpload(e, 'audio', 'audio_preview_url')} disabled={uploading === 'audio'} />
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">WAV (Alta Calidad)</label>
            <div className="flex gap-2">
              <input className="flex-1 input-glass text-sm" value={form.wav_file_url || ''} onChange={e => setForm({ ...form, wav_file_url: e.target.value })} placeholder="URL o subir WAV" data-testid="track-input-wav_file_url" />
              <label className={`btn-secondary py-2 px-3 text-sm cursor-pointer flex items-center gap-1 ${uploading === 'audiowav_file_url' ? 'opacity-50' : ''}`}>
                <Upload className="w-4 h-4" />{uploading === 'audiowav_file_url' ? '...' : 'Subir'}
                <input type="file" accept=".wav,audio/wav" className="hidden" onChange={e => handleUpload(e, 'audio', 'wav_file_url')} disabled={uploading === 'audiowav_file_url'} />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-xs text-white/50 mb-1">BPM</label><input type="number" className="w-full input-glass" value={form.bpm} onChange={e => setForm({ ...form, bpm: e.target.value })} data-testid="track-input-bpm" /></div>
            <div><label className="block text-xs text-white/50 mb-1">Key</label><input className="w-full input-glass" value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} data-testid="track-input-key" /></div>
            {!hideFinancials && <div><label className="block text-xs text-white/50 mb-1">Precio ($)</label><input type="number" step="0.01" className="w-full input-glass" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} data-testid="track-input-price" /></div>}
          </div>
          <div><label className="block text-xs text-white/50 mb-1">Duracion</label><input className="w-full input-glass" value={form.duration} placeholder="3:24" onChange={e => setForm({ ...form, duration: e.target.value })} data-testid="track-input-duration" /></div>
          <div><label className="block text-xs text-white/50 mb-1">Descripcion</label><textarea className="w-full input-glass min-h-[60px]" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="track-input-description" /></div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Tipo de Licencia</label>
            <select className="w-full input-glass" value={form.license_type || 'basic'} onChange={e => setForm({ ...form, license_type: e.target.value })} data-testid="track-input-license_type">
              <option value="basic">Licencia Basica</option>
              <option value="exclusive">Licencia Exclusiva</option>
            </select>
          </div>
          <div className="flex gap-4">
            <button type="button" onClick={() => setForm({ ...form, is_premium: !form.is_premium })}
              className={`flex-1 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.is_premium ? 'bg-[#E8720C]/20 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/40 border border-white/10'}`} data-testid="track-toggle-premium">
              <Crown className="w-4 h-4" />{form.is_premium ? 'Premium' : 'Basico'}
            </button>
            <button type="button" onClick={() => setForm({ ...form, is_visible: !form.is_visible })}
              className={`flex-1 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.is_visible ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`} data-testid="track-toggle-visible">
              {form.is_visible ? <><Eye className="w-4 h-4" />Visible</> : <><EyeOff className="w-4 h-4" />Oculto</>}
            </button>
          </div>
          <button type="submit" disabled={saving} className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="track-form-save">
            <Save className="w-4 h-4" />{saving ? 'Guardando...' : 'Guardar Track'}
          </button>
        </form>
      </div>
    </div>
  );
};

const TracksPanelFull = ({ hideFinancials = false }) => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modal, setModal] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/tracks`, { withCredentials: true });
      setTracks(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (formData) => {
    try {
      if (formData.id) {
        await axios.put(`${API}/api/admin/tracks/${formData.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/tracks`, formData, { withCredentials: true });
      }
      setModal(null);
      load();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este track?')) return;
    try {
      await axios.delete(`${API}/api/admin/tracks/${id}`, { withCredentials: true });
      load();
    } catch (err) { alert('Error eliminando'); }
  };

  const handleToggleVisibility = async (id, current) => {
    try {
      await axios.put(`${API}/api/admin/tracks/${id}/visibility`, { is_visible: !current }, { withCredentials: true });
      load();
    } catch (err) { alert('Error'); }
  };

  const handleTogglePremium = async (id, current) => {
    try {
      await axios.put(`${API}/api/admin/tracks/${id}/premium`, { is_premium: !current }, { withCredentials: true });
      load();
    } catch (err) { alert('Error'); }
  };

  if (loading) return <p className="text-white/40">Cargando tracks...</p>;

  const filtered = tracks.filter(t => !searchTerm || t.title?.toLowerCase().includes(searchTerm.toLowerCase()) || t.artist?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div data-testid="tracks-panel-full">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="text-2xl font-bold">Tracks ({tracks.length})</h2>
        <div className="flex gap-3">
          <button onClick={() => setModal({ data: null })} className="btn-primary py-2 px-5 flex items-center gap-2 text-sm" data-testid="add-track-btn">
            <Plus className="w-4 h-4" /> Agregar Track
          </button>
        </div>
      </div>
      <div className="glass-card p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input className="w-full input-glass pl-11" placeholder="Buscar tracks..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="admin-track-search" />
        </div>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4">Titulo</th><th className="p-4">Artista</th><th className="p-4">Genero</th>
              {!hideFinancials && <th className="p-4">Precio</th>}
              <th className="p-4 text-center">Premium</th><th className="p-4 text-center">Visible</th><th className="p-4 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {filtered.map(track => (
                <tr key={track.id} className={`border-b border-white/5 hover:bg-white/5 ${!track.is_visible ? 'opacity-40' : ''}`} data-testid={`admin-track-row-${track.id}`}>
                  <td className="p-4 font-medium">{track.title}</td>
                  <td className="p-4 text-white/60">{track.artist}</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full text-xs bg-white/10">{track.genre}</span></td>
                  {!hideFinancials && <td className="p-4 gold-text font-medium">${track.price}</td>}
                  <td className="p-4 text-center">
                    <button onClick={() => handleTogglePremium(track.id, track.is_premium)} className={`p-1.5 rounded-lg transition-colors ${track.is_premium ? 'bg-[#E8720C]/20 text-[#E8720C]' : 'bg-white/5 text-white/30'}`} data-testid={`premium-toggle-${track.id}`}>
                      <Crown className="w-4 h-4" />
                    </button>
                  </td>
                  <td className="p-4 text-center">
                    <button onClick={() => handleToggleVisibility(track.id, track.is_visible)} className={`p-1.5 rounded-lg transition-colors ${track.is_visible ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`} data-testid={`visibility-toggle-${track.id}`}>
                      {track.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setModal({ data: track })} className="p-2 hover:bg-white/10 rounded-lg" data-testid={`edit-track-${track.id}`}><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(track.id)} className="p-2 hover:bg-red-500/20 rounded-lg text-red-400" data-testid={`delete-track-${track.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <p className="text-center text-white/40 py-8">Sin tracks</p>}
      </div>
      {modal && <TrackModal track={modal.data} onClose={() => setModal(null)} onSave={handleSave} hideFinancials={hideFinancials} />}
    </div>
  );
};

export default TracksPanelFull;
